import {
	Skeleton,
	useTheme,
	useMediaQuery,
	TableContainer,
	Table,
	TableHead,
	TableBody,
	TableRow,
	TableCell,
	Grid,
	Card,
	CardContent,
} from '@mui/material';
import NumberFormat from 'react-number-format';
import { CerebroLabel, CerebroText } from '../../shared/components';
import { DealDebtSchedule } from '../../shared/models';
import { DateUtils, EnumUtils } from '../../shared/utils';

interface DebtScheduleProps {
	isLoading: boolean;
	debtSchedules: DealDebtSchedule[] | null | undefined;
}

export const DebtSchedule: React.FC<DebtScheduleProps> = ({ isLoading, debtSchedules }) => {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));

	if (isLoading) {
		return (
			<>
				<Skeleton height={60} />
				<Skeleton height={60} />
				<Skeleton height={60} />
			</>
		);
	}

	return isMobile ? (
		<Grid container spacing={2} alignItems="stretch">
			{debtSchedules?.map(schedule => (
				<Grid key={schedule.guid} item xs={12}>
					<Card
						elevation={3}
						sx={{
							display: 'flex',
							justifyContent: 'space-between',
							flexDirection: 'column',
							height: '100%',
							marginBottom: 2,
						}}
					>
						<CardContent sx={{ paddingBottom: '0 !important' }}>
							<Grid container sx={{ marginBottom: 2 }}>
								<Grid item xs={8} sx={{ display: 'flex' }}>
									<CerebroLabel sx={{ fontWeight: 'bold', alignSelf: 'center' }}>
										{EnumUtils.GetFacilityTypeDescription(schedule.facilityType)}
									</CerebroLabel>
								</Grid>
								<Grid item xs={4} sx={{ textAlign: 'right' }}>
									<NumberFormat
										value={schedule.presentBalance}
										displayType="text"
										thousandSeparator={true}
										prefix="$"
									/>
								</Grid>
							</Grid>
							<CerebroText>
								Lender Type: {EnumUtils.GetLenderTypesDescription(schedule.lenderType)}
							</CerebroText>
							<CerebroText>
								Monthly Payment:{' '}
								<NumberFormat
									value={schedule.monthlyPayment}
									displayType="text"
									thousandSeparator={true}
									prefix="$"
								/>
							</CerebroText>
							<CerebroText>
								Maturity Date: {DateUtils.FormatStringAsShortDate(schedule.maturityDate?.toString())}
							</CerebroText>
							<CerebroText>
								Collateral/Security:{' '}
								{EnumUtils.GetCollateralSecurityTypeDescription(schedule.collateralSecurityType)}
							</CerebroText>
							<CerebroText>Refinancing:{schedule.isRefinance ? 'Yes' : 'No'} </CerebroText>
						</CardContent>
					</Card>
				</Grid>
			))}
			{debtSchedules?.length == 0 && (
				<Grid item xs={12}>
					<CerebroText>
						<br />
						No debt schedule items have been added.
						<br />
						&nbsp;
					</CerebroText>
				</Grid>
			)}
		</Grid>
	) : (
		<TableContainer>
			<Table>
				<TableHead>
					<TableRow>
						<TableCell>Lender Type</TableCell>
						<TableCell>Facility Type</TableCell>
						<TableCell>Balance</TableCell>
						<TableCell>Monthly Payment</TableCell>
						<TableCell>Maturity Date</TableCell>
						<TableCell>Collateral/Security</TableCell>
						<TableCell>Refinancing</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{debtSchedules?.map(schedule => (
						<TableRow key={schedule.guid}>
							<TableCell>{EnumUtils.GetLenderTypesDescription(schedule.lenderType)}</TableCell>
							<TableCell>{EnumUtils.GetFacilityTypeDescription(schedule.facilityType)}</TableCell>
							<TableCell>
								<NumberFormat
									value={schedule.presentBalance}
									displayType="text"
									thousandSeparator={true}
									prefix="$"
								/>
							</TableCell>
							<TableCell>
								<NumberFormat
									value={schedule.monthlyPayment}
									displayType="text"
									thousandSeparator={true}
									prefix="$"
								/>
							</TableCell>
							<TableCell>
								{DateUtils.FormatStringAsShortDate(schedule.maturityDate?.toString())}
							</TableCell>
							<TableCell>
								{EnumUtils.GetCollateralSecurityTypeDescription(schedule.collateralSecurityType)}
							</TableCell>
							<TableCell>{schedule.isRefinance ? 'Yes' : 'No'}</TableCell>
						</TableRow>
					))}
					{debtSchedules?.length == 0 && (
						<TableRow>
							<TableCell colSpan={7}>
								<CerebroText>
									<br />
									No debt schedule items have been added.
									<br />
									&nbsp;
								</CerebroText>
							</TableCell>
						</TableRow>
					)}
				</TableBody>
			</Table>
		</TableContainer>
	);
};
